import type { ReactElement } from 'react';
import { EmberDisc } from '../components/EmberDisc';
import { EmberMascot } from '../components/EmberMascot';
import './ConnectionShots.css';

/**
 * The pictures on the homepage's How it works flow (2026-09-09 拍板 as the
 * "If someone lived it too" card; one flow since 2026-09-12; since 2026-10-16
 * the branch after the fork carries every way a conversation reaches past
 * itself, not only the room): each step shows the product surface where it
 * happens — the composer, the page it leaves behind, a page of how it went
 * for others, someone else's page with a note back, a room open in the chat,
 * and Ember asking about the visitor's own page in the thread. They are
 * drawn from the real components' anatomy (ConversationShell, HowItWentPage,
 * ReadPage, RoomThread, KnockCard) at picture scale, not
 * screenshots, so they stay crisp and recolour with the page. The words in
 * them are one made-up telling; they are decoration (aria-hidden) — the copy
 * is the sentence under each picture. Ember stands in the ones where Ember is
 * doing something — beside its bubble, writing the page, opening the room —
 * and not where the person reads (2026-09-14).
 */
export function ConnectionShot({ shot }: { shot: string }): ReactElement | null {
  switch (shot) {
    case 'tell': return <TellShot />;
    case 'page': return <PageShot />;
    case 'went': return <WentShot />;
    case 'read': return <ReadShot />;
    case 'room': return <RoomShot />;
    case 'ask': return <AskShot />;
    default: return null;
  }
}

/* 2 · the page a conversation leaves behind (the 09-10 HowShots drawing, back
   on 2026-09-12 when How it works became one flow). */
function PageShot(): ReactElement {
  return (
    <div className="cs-shot cs-pageshot" aria-hidden="true">
      {/* Ember writing it down, at the page's foot with a pencil (2026-09-14). */}
      <span className="cs-ember-writer"><EmberMascot face="thinking" still interactive={false} className="cs-ember-mascot" /><i className="cs-pencil" /></span>
      <div className="cs-ember-page">
        <div className="cs-ember-art">
          <i className="cs-art-hill" /><i className="cs-art-sun" /><i className="cs-art-far" /><i className="cs-art-door" />
        </div>
        <div className="cs-ember-body">
          <span className="cs-ember-meta">Told in writing · September 6, 2026</span>
          <span className="cs-ember-title">Ten years, and the door</span>
          <p>The morning I handed in my badge, the lobby smelled the same as it had on my first day. I stood there longer than I needed to. Nobody had asked me to stay, and nobody had asked me to go — that was the part I couldn’t explain to anyone at</p>
        </div>
      </div>
    </div>
  );
}

function TellShot(): ReactElement {
  return (
    <div className="cs-shot cs-tell" aria-hidden="true">
      <div className="cs-bubbles">
        <p className="cs-bubble cs-bubble-you">I left the job I spent ten years building. Everyone says I was brave. I don’t feel brave.</p>
        <div className="cs-turn"><EmberDisc face="curious" /><p className="cs-bubble">What did the last week there feel like?</p></div>
      </div>
      <Composer hint="Start anywhere…" />
    </div>
  );
}

function Composer({ hint }: { hint: string }): ReactElement {
  return (
    <div className="cs-composer">
      <span className="cs-composer-hint">{hint}</span>
      <span className="cs-composer-send">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V6" /><path d="m6 12 6-6 6 6" /></svg>
      </span>
    </div>
  );
}

/* 3 · a page of how it went for others, standing on the frame's floor like
   the visitor's own page (HowItWentPage: the kicker, their question as the
   title, the lede, one entry per person with how long they kept telling it)
   (2026-10-16). */
function WentShot(): ReactElement {
  return (
    <div className="cs-shot cs-went" aria-hidden="true">
      <div className="cs-went-page">
        <span className="cs-went-kicker">How it went for others</span>
        <span className="cs-went-title">Was it brave, or did I just leave?</span>
        <p className="cs-went-lede cs-optional">Three people stood close to this, each walking away from something they’d built for years. Ember followed what they said as more came to them.</p>
        <div className="cs-went-entry">
          <span className="cs-went-way">One started over smaller, in the same field.</span>
          <span className="cs-went-told">Told to Ember 5 times over 4 months</span>
          <p className="cs-went-then">The first months felt like a step down. By spring the work felt like hers again.</p>
        </div>
        <div className="cs-went-entry">
          <span className="cs-went-way">One took a year off and still wonders if it was right.</span>
          <span className="cs-went-told">Told to Ember 3 times over 7 weeks</span>
        </div>
      </div>
    </div>
  );
}

/* 4 · someone else's page, read here, and a note back (ReadPage: their page,
   the one-tap thank-you, the note composer) — the same other person as the
   second screen's fourth card (WhyShots) (2026-10-16). */
function ReadShot(): ReactElement {
  return (
    <div className="cs-shot cs-read" aria-hidden="true">
      <div className="cs-ember-page cs-read-page">
        <div className="cs-ember-art cs-art-theirs">
          <i className="cs-art-hill" /><i className="cs-art-sun" /><i className="cs-art-far" />
        </div>
        <div className="cs-ember-body">
          <span className="cs-ember-meta">Told by another person</span>
          <span className="cs-ember-title">The badge in the drawer</span>
          <p>I kept the badge in a drawer for a year. Not because I missed the place. Because throwing it out would have meant admitting I had chosen this.</p>
        </div>
      </div>
      <div className="cs-note">
        <span className="cs-chip">Thank you.</span>
        <Composer hint="A note for them…" />
      </div>
    </div>
  );
}

/* 5 · a room open in the chat: its bar — Back to the Chats list with what is
   unread elsewhere, the room's faces and name — who is there, a few lines,
   the composer (RoomThread anatomy; the bar since the Chats list, 2026-10-20). */
function RoomShot(): ReactElement {
  return (
    <div className="cs-shot cs-room" aria-hidden="true">
      <div className="cs-room-bar">
        <span className="cs-room-back">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m14.5 5-7 7 7 7" /></svg>
          <span className="cs-room-count">2</span>
        </span>
        <span className="cs-faces"><span className="cs-face cs-face-a" /><span className="cs-face cs-face-c" /></span>
        <span className="cs-room-name">Leaving something you built</span>
      </div>
      <div className="cs-room-head">
        <span><b>Wren, Rowan &amp; you</b> · with Ember</span>
        <span className="cs-room-chev">›</span>
      </div>
      <div className="cs-lines">
        <div className="cs-line cs-room-intro"><EmberDisc face="warm" /><p className="cs-bubble">Wren put this one up, and now there are three of you. Say hi whenever you like — I’ll mostly stay out of the way.</p></div>
        <div className="cs-line"><span className="cs-face cs-face-a" /><p className="cs-bubble"><span className="cs-line-name">Wren</span>Four months out and I still open the old Slack in my head every morning.</p></div>
        <div className="cs-line cs-line-you"><p className="cs-bubble cs-bubble-you">Ten years for me. Everyone says brave. I say it to myself and it doesn’t land.</p></div>
        <div className="cs-line cs-optional"><span className="cs-face cs-face-c" /><p className="cs-bubble"><span className="cs-line-name">Rowan</span>Mine wasn’t a job, it was a marriage. Same standing-in-the-lobby feeling, though.</p></div>
      </div>
      <Composer hint="Say something to the room…" />
    </div>
  );
}

/* 6 · Ember asking, in the thread, whether the visitor's own page may be read
   by people it could help, and the yes (KnockCard's two answers) (2026-10-16). */
function AskShot(): ReactElement {
  return (
    <div className="cs-shot cs-ask" aria-hidden="true">
      <div className="cs-bubbles">
        <div className="cs-turn"><EmberDisc face="warm" /><p className="cs-bubble">What you wrote about the door could really help someone standing where you stood. May I let them read it? Never who you are.</p></div>
      </div>
      <div className="cs-chips"><span className="cs-chip">Not this one</span><span className="cs-chip cs-chip-on">Yes, let them</span></div>
    </div>
  );
}
